// <yoj-megamenu>: the yoj menu bar (snippets/yoj-megamenu.liquid). An item's panel opens when the pointer rests on it
// (a short pause, so sweeping across the bar doesn't flicker), on a click or tap, or from the keyboard; it closes when
// the pointer leaves (if hover opened it), on Escape, on a click or tap elsewhere, or when focus moves away. Items that
// don't fit across the bar go into More. Keys: Left/Right/Home/End along the bar, Down into a panel, Up/Down/Home/End
// within it, Escape back to its item.
const OPEN_DELAY = 150; // ms the pointer rests on an item before its panel opens
const SWITCH_DELAY = 80; // ms on another item before the open panel switches to it
const CLOSE_DELAY = 300; // ms after the pointer leaves before a panel hover opened closes

class YojMegamenu extends HTMLElement {
  #open = null; // the item whose panel is open
  #byHover = false; // whether hover opened it
  #timer = 0;
  #watch = null;
  #resize = null;

  connectedCallback() {
    this.#watch = new AbortController();
    const signal = this.#watch.signal;
    this.nav = this.querySelector('.yoj-mega__nav');
    this.bar = this.querySelector('.yoj-mega__bar');
    this.more = this.bar.querySelector(':scope > .yoj-mega__item--more');
    this.items = [...this.bar.children].filter((li) => li !== this.more);
    this.moreGroups = [...this.more.querySelectorAll('.yoj-mega__panel > .yoj-mega__links > .yoj-mega__group')];

    for (const item of [...this.items, this.more]) {
      item.addEventListener('pointerenter', (event) => this.#enter(item, event), { signal });
      item.addEventListener('pointerleave', (event) => this.#leave(item, event), { signal });
      // Focus moving to something outside the item (a null target is a click on nothing, handled below)
      item.addEventListener('focusout', (event) => {
        if (this.#open === item && event.relatedTarget && !item.contains(event.relatedTarget)) this.close();
      }, { signal });
      if (this.#panel(item)) this.#top(item).addEventListener('click', () => this.#click(item), { signal });
    }
    this.addEventListener('keydown', (event) => this.#key(event), { signal });
    document.addEventListener('pointerdown', (event) => {
      if (this.#open && !this.contains(event.target)) this.close();
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#open && !this.contains(event.target)) this.close();
    }, { signal });

    this.#resize = new ResizeObserver(() => this.layout());
    this.#resize.observe(this.nav);
    document.fonts?.ready.then(() => this.isConnected && this.layout());
  }

  disconnectedCallback() {
    this.#watch?.abort();
    this.#resize?.disconnect();
    clearTimeout(this.#timer);
  }

  open(item, how) {
    clearTimeout(this.#timer);
    if (this.#open && this.#open !== item) this.#show(this.#open, false);
    this.#open = item;
    this.#byHover = how === 'hover';
    this.#show(item, true);
    this.#place(item);
  }

  close({ focus = false } = {}) {
    clearTimeout(this.#timer);
    const item = this.#open;
    if (!item) return;
    this.#open = null;
    this.#show(item, false);
    if (focus) this.#top(item).focus();
  }

  // Puts as many items on the bar as fit, in menu order, and the rest in More
  layout() {
    const width = this.bar.clientWidth;
    if (!width) return; // the bar isn't showing (a narrow window)
    for (const item of this.items) item.hidden = false;
    this.more.hidden = false;
    const sizes = this.items.map((item) => item.getBoundingClientRect().width);
    let fit = this.items.length;
    if (sizes.reduce((sum, size) => sum + size, 0) > width + 0.5) {
      let used = this.more.getBoundingClientRect().width;
      fit = 0;
      while (fit < sizes.length && used + sizes[fit] <= width + 0.5) used += sizes[fit++];
    }
    this.items.forEach((item, i) => {
      item.hidden = i >= fit;
      if (this.moreGroups[i]) this.moreGroups[i].hidden = i < fit;
    });
    this.more.hidden = fit === this.items.length;
    // More is marked when it holds the page (or the section) you're on
    const here = this.items.slice(fit).some((item) => this.#top(item).matches('[aria-current], [data-here]'));
    this.#top(this.more).toggleAttribute('data-here', here);
    if (this.#open?.hidden) this.close();
    else if (this.#open) this.#place(this.#open);
  }

  #top(item) {
    return item.querySelector(':scope > .yoj-mega__top');
  }

  #panel(item) {
    return item.querySelector(':scope > .yoj-mega__panel');
  }

  #links(item) {
    return [...this.#panel(item).querySelectorAll('a')].filter((link) => !link.closest('[hidden]'));
  }

  #show(item, open) {
    this.#top(item).setAttribute('aria-expanded', String(open));
    this.#panel(item).hidden = !open;
  }

  // A list panel starts under its item, kept inside the bar; every panel fits in the window's height
  #place(item) {
    const panel = this.#panel(item);
    const nav = this.nav.getBoundingClientRect();
    panel.style.setProperty('--yoj-mega-max-height', `${Math.max(240, Math.floor(innerHeight - nav.bottom - 16))}px`);
    if (panel.classList.contains('yoj-mega__panel--wide')) return;
    const top = this.#top(item).getBoundingClientRect();
    const left = Math.max(0, Math.min(top.left - nav.left, nav.width - panel.offsetWidth));
    panel.style.setProperty('--yoj-mega-left', `${Math.round(left)}px`);
  }

  #enter(item, event) {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return; // a tap opens with its click
    clearTimeout(this.#timer);
    if (this.#open === item) return;
    const panel = this.#panel(item);
    if (!panel && !this.#open) return;
    this.#timer = setTimeout(() => (panel ? this.open(item, 'hover') : this.close()), this.#open ? SWITCH_DELAY : OPEN_DELAY);
  }

  #leave(item, event) {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    clearTimeout(this.#timer);
    if (this.#open === item && this.#byHover) this.#timer = setTimeout(() => this.close(), CLOSE_DELAY);
  }

  #click(item) {
    if (this.#open !== item) return this.open(item, 'click');
    // A click on an item that hover has just opened keeps it open (and a second click closes it)
    if (this.#byHover) {
      clearTimeout(this.#timer);
      this.#byHover = false;
      return;
    }
    this.close();
  }

  #key(event) {
    const { key, target } = event;
    if (key === 'Escape') {
      if (!this.#open) return;
      event.preventDefault();
      this.close({ focus: this.#open.contains(target) });
      return;
    }
    const tops = [...this.items, this.more].filter((item) => !item.hidden).map((item) => this.#top(item));
    const at = tops.indexOf(target);
    if (at >= 0) {
      if (key === 'ArrowRight' || key === 'ArrowLeft') {
        tops[(at + (key === 'ArrowRight' ? 1 : -1) + tops.length) % tops.length].focus();
      } else if (key === 'Home' || key === 'End') {
        tops[key === 'Home' ? 0 : tops.length - 1].focus();
      } else if (key === 'ArrowDown' && this.#panel(target.parentElement)) {
        const item = target.parentElement;
        if (this.#open !== item) this.open(item, 'key');
        this.#links(item)[0]?.focus();
      } else return;
      event.preventDefault();
      return;
    }
    const item = this.#open;
    if (!item || !this.#panel(item).contains(target)) return;
    const links = this.#links(item);
    const i = links.indexOf(target);
    if (i < 0) return;
    if (key === 'ArrowDown') links[Math.min(i + 1, links.length - 1)].focus();
    else if (key === 'ArrowUp') (i === 0 ? this.#top(item) : links[i - 1]).focus();
    else if (key === 'Home' || key === 'End') links[key === 'Home' ? 0 : links.length - 1].focus();
    else return;
    event.preventDefault();
  }
}

if (!customElements.get('yoj-megamenu')) customElements.define('yoj-megamenu', YojMegamenu);
